import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Circle, GoogleMap, Marker, Polygon, Polyline } from "@react-google-maps/api";
import { Download, Pause, Play } from "lucide-react";
import Papa from "papaparse";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { trackBuilderEvent, trackBuilderFailure } from "../../../helpers/builderAnalytics";
import { getDownloadURL, ref as storageRef } from "firebase/storage";
import { firestore, storage } from "../../../helpers/firebaseConfig";
import {
  normalizeRun,
  positiveNumber,
} from "../../../helpers/modelBuilderHelpers";
import { formatTime } from "../../../helpers/generalHelpers";
import { blockedRoadSegments, parseLineStringToPath, pathIntersectsViewportBounds } from "../../../helpers/modelBuilder/mbChatPureUtils";
import useResultsGifExport from "./useResultsGifExport";
import { resultLinkArrow, showLinkTypeAtZoom } from "./visibilityHelpers/linkVisibility";
import { selectResultNodesForRender } from "./visibilityHelpers/nodeVisibility";
import MBResultsSummary from "./MBResultsSummary";
import "./MBResultsMap.css";

const DEFAULT_CENTER = { lat: 37.640244, lng: -122.418184 };
const DEFAULT_STEP = 100;
const DEFAULT_TOTAL_TIME = 3400;
const FIRE_COLORS = ["#f59e0b", "#ef4444", "#a855f7", "#06b6d4"];
const BOTTLENECK_COLOR = "#ef4444";
const BLOCKED_ROAD_COLOR = "#7b8490";
const INACTIVE_LINK_COLOR = "#64748b";
const RESULT_ARROW_PATH = "M -4 -3 L 4 0 L -4 3 L -2 0 Z";
const BLOCKED_ROAD_MARKER = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28"><circle cx="14" cy="14" r="12" fill="#4b5563" stroke="#d1d5db" stroke-width="2"/><path d="M9 9l10 10M19 9L9 19" stroke="#e5e7eb" stroke-width="3" stroke-linecap="round"/></svg>'
)}`;
const COMPLETED_STATUSES = new Set(["done", "complete", "completed"]);

const darkMapStyle = [
  { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#9ca5b3" }] },
  { featureType: "poi", elementType: "all", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#38414e" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#17263c" }] },
];

function dominantContraflowLinks(links) {
  const byRoad = new Map();
  links.filter((link) => link.effectiveLanes > 0).forEach((link) => {
    const endpoints = link.fromNode && link.toNode
      ? [link.fromNode, link.toNode]
      : [link.path[0], link.path[link.path.length - 1]]
        .map((point) => `${point.lat.toFixed(4)},${point.lng.toFixed(4)}`);
    const roadKey = endpoints.sort().join("|");
    const previous = byRoad.get(roadKey);
    if (!previous || link.effectiveLanes > previous.effectiveLanes || (
      link.effectiveLanes === previous.effectiveLanes &&
      link.effectiveLanes - link.originalLanes > previous.effectiveLanes - previous.originalLanes
    )) byRoad.set(roadKey, link);
  });
  return [...byRoad.values()];
}

function queueColor(row) {
  const queue = Number(row?.queue_vehicle_count);
  const length = Number(row?.length);
  const lanes = Number(row?.lanes);
  if (Number.isFinite(length) && length > 0 && Number.isFinite(lanes) && lanes > 0) {
    const fraction = (Math.max(0, queue) * 8) / Math.max(18, length * lanes);
    if (fraction <= 0.25) return "#22c55e";
    if (fraction < 0.5) return "#f59e0b";
    return "#ef4444";
  }
  if (!Number.isFinite(queue)) return "#64748b";
  if (queue > 10) return "#ef4444";
  if (queue > 5) return "#f59e0b";
  return "#22c55e";
}

function runOutputPrefixes(simulationId, run) {
  const data = run?.raw || {};
  const explicit = [
    data?.storagePrefix, data?.storage_prefix,
    data?.output?.storagePrefix, data?.outputPrefix, data?.output_prefix,
    data?.resultSimulationId, data?.result_simulation_id,
  ];
  return [...new Set([
    ...explicit,
    "model_builders/" + simulationId + "/runs/" + run?.id,
    "model_builders/" + simulationId,
    run?.name,
  ].map((value) => String(value || "").replace(/^\/+|\/+$/g, "")).filter(Boolean))];
}

function explicitRunStoragePrefix(run) {
  const data = run?.raw || {};
  return String(
    data.storagePrefix ||
    data.storage_prefix ||
    data.output?.storagePrefix ||
    data.outputPrefix ||
    data.output_prefix ||
    ""
  ).replace(/^\/+|\/+$/g, "");
}

async function downloadFirst(paths, parser = (response) => response.text(), signal) {
  let lastError = null;
  for (const path of paths) {
    try {
      const url = await getDownloadURL(storageRef(storage, path));
      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error("HTTP " + response.status);
      return { value: await parser(response), path };
    } catch (error) {
      if (signal?.aborted) throw error;
      lastError = error;
    }
  }
  throw lastError || new Error("No result artifact was found.");
}

function MBResultsMap({
  simulationId,
  isVisible = true,
  isLoaded,
  loadError,
  selectedLinkIds = new Set(),
  selectedNodeIds = new Set(),
  onToggleLink,
  onToggleNode,
  onClearSelections,
  onContextChange,
}) {
  const [runs, setRuns] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState("");
  const [builderMeta, setBuilderMeta] = useState({});
  const [time, setTime] = useState(0);
  const [links, setLinks] = useState([]);
  const [contraflowResult, setContraflowResult] = useState({ runId: "", links: [] });
  const [blockedRoadResult, setBlockedRoadResult] = useState({ runId: "", links: [] });
  const [nodeResult, setNodeResult] = useState({ runId: "", nodes: [] });
  const [bottleneckResult, setBottleneckResult] = useState({ key: "", rows: [] });
  const [hazards, setHazards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [downloadError, setDownloadError] = useState("");
  const [downloadingOutputs, setDownloadingOutputs] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [showAllNodes, setShowAllNodes] = useState(false);
  const [showAllLinks, setShowAllLinks] = useState(false);
  const [showContraflow, setShowContraflow] = useState(false);
  const [showBlockedRoads, setShowBlockedRoads] = useState(false);
  const mapRef = useRef(null);
  const mapStageRef = useRef(null);
  const [loadedTime, setLoadedTime] = useState(null);
  const fittedRunIdRef = useRef("");
  const linksRunIdRef = useRef("");
  const networkCacheRef = useRef({ runId: "", links: [] });
  const loadSequenceRef = useRef(0);
  const selectedRunRef = useRef(null);
  const unavailableRunIdsRef = useRef(new Set());
  const [viewportBounds, setViewportBounds] = useState(null);
  const [mapZoom, setMapZoom] = useState(14);

  useEffect(() => {
    if (!isVisible) setPlaying(false);
    else if (mapRef.current && window.google?.maps) window.google.maps.event.trigger(mapRef.current, "resize");
  }, [isVisible]);

  useEffect(() => {
    if (!simulationId) return undefined;
    return onSnapshot(
      collection(firestore, "model_builders", simulationId, "results"),
      (snap) => {
        const next = snap.docs.map((item) => ({ ...normalizeRun(item), raw: item.data() || {} }))
          .filter((run) => COMPLETED_STATUSES.has(run.status))
          .sort((a, b) => b.createdAtMs - a.createdAtMs);
        setRuns(next);
        setSelectedRunId((current) => next.some((run) => run.id === current) ? current : (next[0]?.id || ""));
      },
      (snapshotError) => {
        trackBuilderFailure("builder_results_load_failed", { builder_id: simulationId, tab: "results", endpoint: "results_snapshot" }, snapshotError);
        setRuns([]);
        setSelectedRunId("");
        setError("Could not load simulation results (" + String(snapshotError?.message || snapshotError) + ").");
      }
    );
  }, [simulationId]);

  useEffect(() => {
    if (!simulationId) return undefined;
    return onSnapshot(
      doc(firestore, "model_builders", simulationId),
      (snap) => setBuilderMeta(snap.data() || {}),
      (snapshotError) => {
        setBuilderMeta({});
        console.warn("Failed to load builder timing metadata:", snapshotError);
      }
    );
  }, [simulationId]);

  const selectedRun = useMemo(
    () => runs.find((run) => run.id === selectedRunId) || null,
    [runs, selectedRunId]
  );
  const nodes = useMemo(() => nodeResult.runId === selectedRunId ? nodeResult.nodes : [], [nodeResult, selectedRunId]);
  const contraflowLinks = contraflowResult.runId === selectedRunId ? contraflowResult.links : [];
  const visibleContraflowLinks = dominantContraflowLinks(contraflowLinks);
  const visibleBlockedRoadSegments = useMemo(
    () => blockedRoadSegments(blockedRoadResult.runId === selectedRunId ? blockedRoadResult.links : []),
    [blockedRoadResult, selectedRunId]
  );
  const hasBlockedRoadOverlay = visibleBlockedRoadSegments.length > 0;
  const bottleneckKey = `${selectedRunId}:${time}`;
  const bottlenecks = bottleneckResult.key === bottleneckKey ? bottleneckResult.rows : [];
  selectedRunRef.current = selectedRun;
  const outputDownloadPrefix = selectedRun?.raw?.downloadOutputs === true
    ? explicitRunStoragePrefix(selectedRun)
    : "";
  const step = positiveNumber(selectedRun?.raw?.timeInterval, selectedRun?.raw?.time_interval, builderMeta.timeInterval, builderMeta.time_interval) || DEFAULT_STEP;
  const totalTime = positiveNumber(
    selectedRun?.raw?.totalTime,
    selectedRun?.raw?.total_time,
    selectedRun?.raw?.progress?.latestAvailableTime,
    selectedRun?.raw?.progress?.latest_available_time,
    selectedRun?.raw?.latestAvailableTime,
    selectedRun?.raw?.latest_available_time,
    builderMeta.totalTime,
    builderMeta.total_time
  ) || DEFAULT_TOTAL_TIME;

  const { start: startGifExport, finish: finishGifExport, exporting: exportingGif, progress: exportProgress } = useResultsGifExport({
    stageRef: mapStageRef, simulationId, selectedRunId, totalTime, time, loadedTime,
    loading, isVisible, setTime, setPlaying, setError: setDownloadError,
  });

  useEffect(() => {
    setTime(0);
    setPlaying(false);
    setLinks([]);
    linksRunIdRef.current = "";
    setShowAllNodes(false);
    setShowAllLinks(false);
    setShowContraflow(false);
    setShowBlockedRoads(false);
    setBottleneckResult({ key: "", rows: [] });
    setDownloadError("");
    setDownloadingOutputs(false);
    onClearSelections?.();
    fittedRunIdRef.current = "";
  }, [selectedRunId, onClearSelections]);

  const downloadSimulationOutputs = useCallback(async () => {
    if (!outputDownloadPrefix || downloadingOutputs) return;
    setDownloadingOutputs(true);
    setDownloadError("");
    try {
      const url = await getDownloadURL(storageRef(storage, `${outputDownloadPrefix}/simulation_data.zip`));
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const objectUrl = URL.createObjectURL(await response.blob());
      const safeName = (value) => String(value || "").trim().replace(/[<>:"/\\|?*\x00-\x1F]/g, "_");
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = `${safeName(selectedRun?.name) || "simulation"}_${safeName(selectedRunId)}_data.zip`;
      anchor.rel = "noopener";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
      trackBuilderEvent("builder_results_downloaded", { builder_id: simulationId, tab: "results", run_id: selectedRunId });
    } catch (downloadFailure) {
      trackBuilderFailure("builder_results_download_failed", { builder_id: simulationId, tab: "results", run_id: selectedRunId, endpoint: "simulation_data_zip" }, downloadFailure);
      setDownloadError(`Could not download simulation outputs (${String(downloadFailure?.message || downloadFailure)}).`);
    } finally {
      setDownloadingOutputs(false);
    }
  }, [downloadingOutputs, outputDownloadPrefix, selectedRun?.name, selectedRunId, simulationId]);

  useEffect(() => {
    const controller = new AbortController();
    const run = selectedRun;
    if (!run) {
      return () => controller.abort();
    }
    const prefixes = runOutputPrefixes(simulationId, run);
    const nodesFile = run.runLayers?.nodesFileName || "berkeley_nodes.csv";
    const paths = [
      ...prefixes.map((prefix) => prefix + "/inputs/" + nodesFile),
      "model_builders/" + simulationId + "/inputs/" + nodesFile,
    ];
    downloadFirst(paths, undefined, controller.signal).then(({ value, path }) => {
      if (controller.signal.aborted) return;
      const isRunInput = path.startsWith(`${explicitRunStoragePrefix(run)}/inputs/`);
      const parsed = Papa.parse(value, { header: true, skipEmptyLines: true }).data.map((row) => {
        const id = String(row.node_id || row.nodeid || row.id || "");
        const lat = Number(row.lat ?? row.latitude);
        const lng = Number(row.lon ?? row.lng ?? row.longitude);
        return { id, lat, lng, isDestination: isRunInput && String(row.type || "").trim().toLowerCase() === "destination" };
      }).filter((node) => node.id && Number.isFinite(node.lat) && Number.isFinite(node.lng));
      setNodeResult({ runId: run.id, nodes: parsed });
    }).catch((nodeError) => {
      if (!controller.signal.aborted) {
        setNodeResult({ runId: run.id, nodes: [] });
        console.warn("Could not load result-map nodes:", nodeError);
      }
    });
    return () => controller.abort();
  }, [selectedRun, simulationId]);

  useEffect(() => {
    const controller = new AbortController();
    const prefix = explicitRunStoragePrefix(selectedRun);
    if (!prefix) return () => controller.abort();
    downloadFirst([`${prefix}/inputs/contraflow_links.csv`], undefined, controller.signal)
      .then(({ value }) => {
        if (controller.signal.aborted) return;
        const parsed = Papa.parse(value, { header: true, skipEmptyLines: true, transformHeader: (header) => header.trim().replace(/^\uFEFF/, "") }).data
          .map((row) => ({
            id: String(row.link_id || "").trim(),
            path: (parseLineStringToPath(row.geometry) || []),
            fromNode: String(row.from_node ?? row.source ?? row.u ?? "").trim(),
            toNode: String(row.to_node ?? row.target ?? row.v ?? "").trim(),
            originalLanes: Number(row.original_lanes),
            effectiveLanes: Number(row.effective_lanes),
          }))
          .filter((row) => row.id && row.path.length > 1 && Number.isFinite(row.originalLanes) && Number.isFinite(row.effectiveLanes));
        setContraflowResult({ runId: selectedRun.id, links: parsed });
      })
      .catch(() => { if (!controller.signal.aborted) setContraflowResult({ runId: selectedRun.id, links: [] }); });
    return () => controller.abort();
  }, [selectedRun]);

  useEffect(() => {
    const controller = new AbortController();
    const run = selectedRun;
    const prefix = explicitRunStoragePrefix(run);
    if (!run || !prefix) {
      setBlockedRoadResult({ runId: "", links: [] });
      return () => controller.abort();
    }
    const loadBlockedRoads = async () => {
      try {
        const { value: reportCsv } = await downloadFirst([`${prefix}/inputs/blocked_road_links.csv`], undefined, controller.signal);
        let inputLinks = new Map();
        try {
          const { value: inputCsv } = await downloadFirst([`${prefix}/inputs/berkeley_links.csv`], undefined, controller.signal);
          inputLinks = new Map(Papa.parse(inputCsv, { header: true, skipEmptyLines: true, transformHeader: (header) => header.trim().replace(/^\uFEFF/, "") }).data
            .map((row) => [String(row.link_id || "").trim(), row]));
        } catch (error) {
          if (controller.signal.aborted) return;
        }
        const reportRows = Papa.parse(reportCsv, { header: true, skipEmptyLines: true, transformHeader: (header) => header.trim().replace(/^\uFEFF/, "") }).data;
        const parsed = reportRows.map((row) => {
            const id = String(row.link_id || "").trim();
            const input = inputLinks.get(id) || {};
            const reportPath = (parseLineStringToPath(row.geometry) || []);
            return {
              id,
              name: String(row.name || input.name || "").trim(),
              startNodeId: String(row.start_node_id ?? input.start_node_id ?? input.from_node ?? "").trim(),
              endNodeId: String(row.end_node_id ?? input.end_node_id ?? input.to_node ?? "").trim(),
              path: reportPath.length > 1 ? reportPath : (parseLineStringToPath(input.geometry) || []),
            };
          })
          .filter((row) => row.id && row.path.length > 1);
        if (!controller.signal.aborted) setBlockedRoadResult({ runId: run.id, links: parsed });
      } catch (error) {
        if (!controller.signal.aborted) setBlockedRoadResult({ runId: run.id, links: [] });
      }
    };
    loadBlockedRoads();
    return () => controller.abort();
  }, [selectedRun]);

  useEffect(() => {
    if (!playing || !selectedRunId || loading) return undefined;
    const timer = window.setInterval(() => {
      setTime((current) => current >= totalTime ? 0 : Math.min(totalTime, current + step));
    }, 700);
    return () => window.clearInterval(timer);
  }, [playing, selectedRunId, step, totalTime, loading]);

  useEffect(() => {
    const controller = new AbortController();
    const prefix = explicitRunStoragePrefix(selectedRunRef.current);
    if (!prefix) return () => controller.abort();
    downloadFirst([`${prefix}/bottleneck_stats/bbase-tester_at_${time}.csv`], undefined, controller.signal)
      .then(({ value }) => {
        if (controller.signal.aborted) return;
        const rows = Papa.parse(value, { header: true, skipEmptyLines: true, transformHeader: (header) => header.trim().replace(/^\uFEFF/, "") }).data
          .map((row) => ({
            nodeId: String(row.node_id || "").trim(),
            strength: Number(row.total_queued_vehicle_seconds),
          }))
          .filter((row) => row.nodeId && Number.isFinite(row.strength) && row.strength > 0);
        setBottleneckResult({ key: bottleneckKey, rows });
      })
      .catch(() => { if (!controller.signal.aborted) setBottleneckResult({ key: bottleneckKey, rows: [] }); });
    return () => controller.abort();
  }, [bottleneckKey]);

  const loadResults = useCallback(async (signal, sequence) => {
    const isCurrent = () => !signal.aborted && loadSequenceRef.current === sequence;
    const run = selectedRunRef.current;
    if (!run || run.id !== selectedRunId) {
      setLinks([]);
      setHazards([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadedTime(null);
    setError("");
    const prefixes = runOutputPrefixes(simulationId, run);
    const outputPaths = prefixes.map((prefix) => prefix + "/link_stats/lbase-tester_at_" + time + ".csv");
    const linksFile = run.runLayers?.linksFileName || "berkeley_links.csv";
    const inputPaths = [...new Set([
      ...prefixes.map((prefix) => prefix + "/inputs/" + linksFile),
      "model_builders/" + simulationId + "/inputs/" + linksFile,
    ])];
    try {
      let networkLinks = networkCacheRef.current.runId === run.id ? networkCacheRef.current.links : [];
      if (!networkLinks.length) {
        const { value: networkCsv } = await downloadFirst(inputPaths, undefined, signal);
        networkLinks = Papa.parse(networkCsv, { header: true, skipEmptyLines: true, transformHeader: (header) => header.trim().replace(/^\uFEFF/, "") }).data
          .map((row) => ({
            id: String(row.link_id || row.id || "").trim(),
            path: parseLineStringToPath(row.geometry) || [],
            type: String(row.type ?? row.road_type ?? row.highway ?? "").trim(),
            startNodeId: String(row.start_node_id ?? row.from_node_id ?? row.from_node ?? row.source ?? row.u ?? "").trim(),
            endNodeId: String(row.end_node_id ?? row.to_node_id ?? row.to_node ?? row.target ?? row.v ?? "").trim(),
          }))
          .filter((row) => row.id && row.path.length > 1);
        if (!networkLinks.length) throw new Error("The selected links layer has no drawable geometry.");
        networkCacheRef.current = { runId: run.id, links: networkLinks };
      }
      if (!isCurrent()) return;
      const alreadyShowingRun = linksRunIdRef.current === run.id;
      linksRunIdRef.current = run.id;
      if (!alreadyShowingRun) setLinks(networkLinks);
      try {
        const { value: statsCsv } = await downloadFirst(outputPaths, undefined, signal);
        const statsById = new Map(Papa.parse(statsCsv, { header: true, skipEmptyLines: true, transformHeader: (header) => header.trim().replace(/^\uFEFF/, "") }).data
          .map((row) => [String(row.link_id || row.id || "").trim(), row]));
        if (!isCurrent()) return;
        setLinks(networkLinks.map((link) => ({ ...link, color: statsById.has(link.id) ? queueColor(statsById.get(link.id)) : null })));
      } catch (outputError) {
        if (isCurrent()) {
          if (!unavailableRunIdsRef.current.has(run.id)) {
            unavailableRunIdsRef.current.add(run.id);
            trackBuilderFailure("builder_results_time_unavailable", { builder_id: simulationId, tab: "results", run_id: run.id, time_seconds: time, endpoint: "link_stats" }, outputError);
          }
          setError("Traffic results for " + formatTime(time) + " are not available yet.");
        }
      }
    } catch (loadErrorValue) {
      if (!isCurrent()) return;
      setLinks([]);
      trackBuilderFailure("builder_results_layer_failed", { builder_id: simulationId, tab: "results", run_id: run.id, endpoint: "links_layer" }, loadErrorValue);
      setError("Could not load this run's links layer (" + String(loadErrorValue?.message || loadErrorValue) + ").");
    }

    if (!isCurrent()) return;
    const firePaths = prefixes.map((prefix) => prefix + "/fire_inputs/fire_" + time + ".geojson");
    try {
      const { value } = await downloadFirst(firePaths, (response) => response.json(), signal);
      const nextHazards = (value?.features || []).flatMap((feature, featureIndex) => {
        const geometry = feature?.geometry || {};
        const polygons = String(geometry.type).toLowerCase() === "polygon"
          ? [geometry.coordinates]
          : String(geometry.type).toLowerCase() === "multipolygon" ? geometry.coordinates : [];
        return polygons.map((polygon, polygonIndex) => ({
          id: featureIndex + "-" + polygonIndex,
          paths: (polygon?.[0] || []).map(([lng, lat]) => ({ lat: Number(lat), lng: Number(lng) })),
          color: FIRE_COLORS[featureIndex % FIRE_COLORS.length],
        })).filter((polygon) => polygon.paths.length >= 3);
      });
      if (isCurrent()) setHazards(nextHazards);
    } catch (_) {
      if (isCurrent()) setHazards([]);
    } finally {
      if (isCurrent()) {
        setLoadedTime(time);
        setLoading(false);
      }
    }
  }, [selectedRunId, simulationId, time]);

  useEffect(() => {
    const controller = new AbortController();
    const sequence = loadSequenceRef.current + 1;
    loadSequenceRef.current = sequence;
    loadResults(controller.signal, sequence);
    return () => controller.abort();
  }, [loadResults]);

  const fitSelectedRun = useCallback((map) => {
    if (
      !isVisible ||
      !map ||
      !window.google?.maps ||
      !links.length ||
      !selectedRunId ||
      linksRunIdRef.current !== selectedRunId ||
      fittedRunIdRef.current === selectedRunId
    ) return;
    const bounds = new window.google.maps.LatLngBounds();
    links.forEach((link) => link.path.forEach((point) => bounds.extend(point)));
    map.fitBounds(bounds);
    fittedRunIdRef.current = selectedRunId;
  }, [isVisible, links, selectedRunId]);
  useEffect(() => { fitSelectedRun(mapRef.current); }, [fitSelectedRun]);

  const updateViewportBounds = useCallback(() => {
    const bounds = mapRef.current?.getBounds?.();
    const ne = bounds?.getNorthEast?.();
    const sw = bounds?.getSouthWest?.();
    if (!ne || !sw) return;
    setViewportBounds({ north: ne.lat(), south: sw.lat(), east: ne.lng(), west: sw.lng() });
  }, []);

  const linksForRender = useMemo(() => links.filter((link) => {
    if (selectedLinkIds.has(link.id) || link.color) return true;
    return showAllLinks && viewportBounds && showLinkTypeAtZoom(link.type, mapZoom)
      && pathIntersectsViewportBounds(link.path, viewportBounds);
  }).sort((a, b) => Number(selectedLinkIds.has(a.id)) - Number(selectedLinkIds.has(b.id))), [links, mapZoom, selectedLinkIds, showAllLinks, viewportBounds]);
  const linkArrows = useMemo(() => {
    const nodeById = new Map(nodes.map((node) => [node.id, node]));
    return linksForRender.flatMap((link) => {
      if (!link.color && !selectedLinkIds.has(link.id)) return [];
      const arrow = resultLinkArrow(link, nodeById.get(link.startNodeId), nodeById.get(link.endNodeId), mapZoom);
      if (!arrow) return [];
      const { lat, lng } = arrow.position;
      if (viewportBounds && (lat < viewportBounds.south || lat > viewportBounds.north || lng < viewportBounds.west || lng > viewportBounds.east)) return [];
      return [{ id: link.id, ...arrow }];
    });
  }, [linksForRender, nodes, mapZoom, selectedLinkIds, viewportBounds]);
  const nodesForRender = useMemo(() => selectResultNodesForRender({
    nodes, viewportBounds, selectedNodeIds, showNodes: showAllNodes, zoom: mapZoom,
  }), [nodes, viewportBounds, selectedNodeIds, showAllNodes, mapZoom]);
  const destinationNodes = useMemo(() => nodes.filter((node) => node.isDestination), [nodes]);
  const bottleneckNodes = useMemo(() => {
    const nodeById = new Map(nodes.map((node) => [node.id, node]));
    const drawable = bottlenecks.flatMap((row) => {
      const node = nodeById.get(row.nodeId);
      return node ? [{ ...node, strength: row.strength }] : [];
    });
    return drawable.sort((a, b) => b.strength - a.strength || a.id.localeCompare(b.id))
      .slice(0, Math.ceil(drawable.length * 0.1));
  }, [bottlenecks, nodes]);
  const activeEntityType = selectedNodeIds.size && !selectedLinkIds.size ? "nodes" : "links";
  const hasTrafficColors = links.some((link) => !!link.color);
  useEffect(() => {
    onContextChange?.({
      source: "simulation_results",
      runId: selectedRun?.id || "",
      runName: selectedRun?.name || "",
      timeSeconds: time,
      entityType: activeEntityType,
      linksLayerId: selectedRun?.runLayers?.linksLayerId || selectedRun?.runLayers?.linksFileName || "",
      nodesLayerId: selectedRun?.runLayers?.nodesLayerId || selectedRun?.runLayers?.nodesFileName || "",
      layerId: activeEntityType === "nodes"
        ? selectedRun?.runLayers?.nodesLayerId || selectedRun?.runLayers?.nodesFileName
        : selectedRun?.runLayers?.linksLayerId || selectedRun?.runLayers?.linksFileName,
    });
  }, [activeEntityType, onContextChange, selectedRun, time]);

  if (loadError) return <div className="mb-results-empty">Google Maps failed to load.</div>;
  if (!isLoaded) return <div className="mb-results-empty">Loading map…</div>;

  return (
    <div className="mb-results-mapShell" style={{ display: isVisible ? undefined : "none" }}>
      <div className="mb-results-mapViewport">
      <div className="mb-results-timeline">
        <button className="mb-results-playButton" type="button" aria-label={playing ? "Pause playback" : "Play playback"} title={playing ? "Pause" : "Play"} onClick={() => { trackBuilderEvent(playing ? "builder_results_play_paused" : "builder_results_play_started", { builder_id: simulationId, tab: "results", run_id: selectedRunId, time_seconds: time }); setPlaying((value) => !value); }} disabled={!selectedRun || exportingGif}>{playing ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" />}</button>
        <input type="range" min={0} max={Math.max(0, totalTime)} step={Math.max(1, step)} value={Math.min(time, totalTime)} onChange={(event) => setTime(Number(event.target.value))} onPointerUp={(event) => trackBuilderEvent("builder_results_time_changed", { builder_id: simulationId, tab: "results", run_id: selectedRunId, time_seconds: Number(event.currentTarget.value) })} onKeyUp={(event) => { if (["ArrowLeft", "ArrowRight", "Home", "End", "PageUp", "PageDown"].includes(event.key)) trackBuilderEvent("builder_results_time_changed", { builder_id: simulationId, tab: "results", run_id: selectedRunId, time_seconds: Number(event.currentTarget.value) }); }} disabled={!selectedRun || exportingGif} />
        <strong className="mb-results-time">{formatTime(time)}</strong>
        <span className="mb-results-loading" aria-live="polite" aria-hidden={!loading}>
          {loading ? (hasTrafficColors ? "Loading…" : "Loading traffic…") : "\u00a0"}
        </span>
        <button type="button" className="mb-results-gifButton" onClick={exportingGif ? finishGifExport : startGifExport} disabled={!selectedRun} title={exportingGif ? "Save the GIF captured so far" : "Export the visible map playback as GIF"}>{exportingGif ? `Save GIF (${exportProgress}%)` : "Export GIF"}</button>
      </div>
      <div className="mb-results-mapStage" ref={mapStageRef}>
        {exportingGif && <div className="mb-results-exportTime">Simulation time {formatTime(time, { style: "digital" })}</div>}
        <div className="mb-results-toolbar">
          <select aria-label="Simulation run" value={selectedRunId} onChange={(event) => { setSelectedRunId(event.target.value); trackBuilderEvent("builder_results_run_selected", { builder_id: simulationId, tab: "results", run_id: event.target.value }); }}>
            {!runs.length && <option value="">No completed simulation runs yet</option>}
            {runs.map((run) => <option key={run.id} value={run.id}>{run.name}</option>)}
          </select>
          {!!outputDownloadPrefix && <button type="button" className="mb-results-download" onClick={downloadSimulationOutputs} disabled={downloadingOutputs} aria-label="Download simulation outputs" title="Download simulation outputs"><Download size={17} /></button>}
        </div>
      <GoogleMap
        mapContainerClassName="mb-results-map"
        center={DEFAULT_CENTER}
        zoom={14}
        onLoad={(map) => { mapRef.current = map; fitSelectedRun(map); }}
        onUnmount={() => { mapRef.current = null; }}
        onIdle={updateViewportBounds}
        onZoomChanged={() => setMapZoom(mapRef.current?.getZoom() || 14)}
        options={{ styles: darkMapStyle, clickableIcons: false, streetViewControl: false, mapTypeControl: false, fullscreenControl: false, gestureHandling: "cooperative" }}
      >
        {linksForRender.map((link) => (
          <Polyline key={link.id} path={link.path} onClick={() => onToggleLink?.(link.id)} options={{ strokeColor: selectedLinkIds.has(link.id) ? "#38bdf8" : link.color || INACTIVE_LINK_COLOR, strokeOpacity: link.color || selectedLinkIds.has(link.id) ? 0.9 : 0.55, strokeWeight: selectedLinkIds.has(link.id) ? 8 : link.color ? 4 : 2, zIndex: selectedLinkIds.has(link.id) ? 4 : link.color ? 1 : 0 }} />
        ))}
        {linkArrows.map((arrow) => <Marker key={`direction-${arrow.id}`} position={arrow.position} onClick={() => onToggleLink?.(arrow.id)} zIndex={selectedLinkIds.has(arrow.id) ? 6 : 3} icon={{ path: RESULT_ARROW_PATH, anchor: new window.google.maps.Point(0, 0), rotation: arrow.rotation, fillColor: selectedLinkIds.has(arrow.id) ? "#38bdf8" : "#ffffff", fillOpacity: 1, strokeColor: "#0f172a", strokeWeight: 0.8, scale: selectedLinkIds.has(arrow.id) ? 2.3 : 2 }} title={`Link ${arrow.id}: select direction`} />)}
        {showContraflow && visibleContraflowLinks.map((link) => (
          <Polyline key={`contraflow-${link.id}`} path={link.path} onClick={() => onToggleLink?.(link.id)} options={{ strokeColor: selectedLinkIds.has(link.id) ? "#38bdf8" : "#a78bfa", strokeOpacity: 1, strokeWeight: selectedLinkIds.has(link.id) ? 9 : 6, zIndex: selectedLinkIds.has(link.id) ? 6 : 5, icons: [{ icon: { path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW, fillColor: "#ffffff", fillOpacity: 1, strokeColor: "#ffffff", strokeWeight: 1, scale: 3 }, offset: "50%", repeat: "100px" }] }} />
        ))}
        {hasBlockedRoadOverlay && showBlockedRoads && visibleBlockedRoadSegments.map((segment) => (
          <React.Fragment key={`blocked-road-${segment.id}`}>
            <Polyline path={segment.path} options={{ strokeColor: BLOCKED_ROAD_COLOR, strokeOpacity: 0.8, strokeWeight: 6, clickable: false, zIndex: 7 }} />
            {segment.arrow && <Marker position={segment.arrow.position} clickable={false} zIndex={9} icon={{ path: "M -3 -2 L 3 0 L -3 2 L -1 0 Z", anchor: new window.google.maps.Point(0, 0), rotation: segment.arrow.rotation, fillColor: "#ffffff", fillOpacity: 1, strokeColor: "#4b5563", strokeWeight: 0.6, scale: 3 }} />}
            {segment.center && <Marker position={segment.center} clickable={false} zIndex={10} icon={{ url: BLOCKED_ROAD_MARKER, scaledSize: new window.google.maps.Size(28, 28), anchor: new window.google.maps.Point(14, 14) }} />}
          </React.Fragment>
        ))}
        {!playing && bottleneckNodes.map((node) => <Circle key={`bottleneck-${node.id}`} center={{ lat: node.lat, lng: node.lng }} radius={14} onClick={() => onToggleNode?.(node.id)} options={{ fillColor: BOTTLENECK_COLOR, fillOpacity: 0.9, strokeColor: "#ffffff", strokeOpacity: 0.9, strokeWeight: 1, zIndex: 8 }} />)}
        {nodesForRender.map((node) => {
          const selected = selectedNodeIds.has(node.id);
          return <Circle key={node.id} center={{ lat: node.lat, lng: node.lng }} radius={selected ? 18 : 7} onClick={() => onToggleNode?.(node.id)} options={{ fillColor: selected ? "#38bdf8" : "#e2e8f0", fillOpacity: selected ? 1 : 0.72, strokeColor: selected ? "#ffffff" : "#475569", strokeOpacity: 1, strokeWeight: selected ? 3 : 1, zIndex: selected ? 7 : 3 }} />;
        })}
        {destinationNodes.map((node) => {
          const selected = selectedNodeIds.has(node.id);
          return <Marker key={`destination-${node.id}`} position={{ lat: node.lat, lng: node.lng }} onClick={() => onToggleNode?.(node.id)} icon={{ path: window.google.maps.SymbolPath.CIRCLE, scale: selected ? 10 : 8, fillColor: selected ? "#38bdf8" : "#facc15", fillOpacity: 1, strokeColor: "#ffffff", strokeWeight: selected ? 3 : 2 }} zIndex={9} />;
        })}
        {hazards.map((hazard) => (
          <Polygon key={hazard.id} paths={hazard.paths} options={{ fillColor: hazard.color, fillOpacity: 0.32, strokeColor: hazard.color, strokeOpacity: 0.9, strokeWeight: 2 }} />
        ))}
      </GoogleMap>

      {(downloadError || error) && <div className="mb-results-notice">{downloadError || error}</div>}
      <div className="mb-results-overlays">
      <div className="mb-results-legend">
        {hasTrafficColors && <div><span className="low" />Low traffic <span className="medium" />Moderate <span className="high" />High</div>}
        {showContraflow && visibleContraflowLinks.length > 0 && <div><span className="contraflow" />Contraflow direction →</div>}
        {showBlockedRoads && visibleBlockedRoadSegments.length > 0 && <div><span className="blockedRoad" aria-hidden="true">×</span>Blocked roads</div>}
        {!!destinationNodes.length && <div><span className="destination" />Destinations</div>}
        {!playing && !!bottleneckNodes.length && <div><span className="bottleneck" />Top 10% bottlenecks</div>}
      </div>
      {(!!links.length || !!nodes.length || !!contraflowLinks.length || hasBlockedRoadOverlay) && <div className="mb-results-selectionTools">
        {!!links.length && <label><input type="checkbox" checked={showAllLinks} onChange={(event) => { setShowAllLinks(event.target.checked); trackBuilderEvent("builder_results_overlay_changed", { builder_id: simulationId, tab: "results", run_id: selectedRunId, overlay: "all_links", enabled: event.target.checked }); }} />Links</label>}
        {!!nodes.length && <label><input type="checkbox" checked={showAllNodes} onChange={(event) => { setShowAllNodes(event.target.checked); trackBuilderEvent("builder_results_overlay_changed", { builder_id: simulationId, tab: "results", run_id: selectedRunId, overlay: "nodes", enabled: event.target.checked }); }} />Nodes</label>}
        {!!contraflowLinks.length && <label><input type="checkbox" checked={showContraflow} onChange={(event) => { setShowContraflow(event.target.checked); trackBuilderEvent("builder_results_overlay_changed", { builder_id: simulationId, tab: "results", run_id: selectedRunId, overlay: "contraflow", enabled: event.target.checked }); }} />Contraflow</label>}
        {hasBlockedRoadOverlay && <label><input type="checkbox" checked={showBlockedRoads} onChange={(event) => { setShowBlockedRoads(event.target.checked); trackBuilderEvent("builder_results_overlay_changed", { builder_id: simulationId, tab: "results", run_id: selectedRunId, overlay: "blocked_roads", enabled: event.target.checked }); }} />Blocked</label>}
        {(selectedLinkIds.size > 0 || selectedNodeIds.size > 0) && <button type="button" onClick={onClearSelections}>Clear ({selectedLinkIds.size + selectedNodeIds.size})</button>}
      </div>}
      </div>
      </div>
      </div>
      <MBResultsSummary
        builderId={simulationId}
        run={selectedRun}
        prefixes={runOutputPrefixes(simulationId, selectedRun)}
        totalTime={totalTime}
        step={step}
        selectedTime={time}
        destinationNodes={destinationNodes}
        isVisible={isVisible}
        selectedNodeIds={selectedNodeIds}
        selectedLinkIds={selectedLinkIds}
        onToggleNode={onToggleNode}
        onToggleLink={onToggleLink}
      />
    </div>
  );
}

export default React.memo(MBResultsMap);
